import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import toast from "react-hot-toast";
import moment from "moment";
import { MdModeEdit, MdPreview } from "react-icons/md";
import { GiCheckMark } from "react-icons/gi";
import "../../styles/gameSession.css";
import GameSessionSeriveces from "../../services/GameSessionSeriveces";
import Loader from "../../components/Loader";
import NoData from "../../components/NoData";

const STATUS_META = {
  PENDING: { label: "PENDING", className: "status-pending" },
  RUNNING: { label: "RUNNING", className: "status-running" },
  COMPLETED: { label: "COMPLETED", className: "status-completed" },
};

const GameSession = () => {
  const gameSercApi = new GameSessionSeriveces();
  const history = useHistory();
  const [activeTab, setActiveTab] = useState("3min");
  const [activeDate, setActiveDate] = useState("today");
  const [percentageInput, setPercentageInput] = useState("");
  const [multiUpdateLoader, setMultiUpdateLoader] = useState(false);
  const [updateSessLoader, setUpdateSessLoader] = useState(false);
  // const [lastSyncAt, setLastSyncAt] = useState(null);
  const lastSyncRef = useRef(null);

  const [sessions, setSessions] = useState([]);
  const [visibleCount, setVisibleCount] = useState(60);
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [loading, setLoading] = useState(false);
  const sessionCardRefs = useRef({});
  const didAutoScrollRef = useRef(false);

  const setSessionCardRef = (id) => (el) => {
    if (!id) return;
    if (el) sessionCardRefs.current[id] = el;
    else delete sessionCardRefs.current[id];
  };
  const formatSessionTime = (start, end) => {
    return `${moment.utc(start).local().format("hh:mm A")} - ${moment
      .utc(end)
      .local()
      .format("hh:mm A")}`;
  };

  const getGameSessionList = async () => {
    setLoading(true);
    gameSercApi
      .getGameSessionData(activeTab, activeDate)
      .then((response) => {
        if (response.isSuccess) {
          setSessions(response.data || []);
          setVisibleCount(60);
          didAutoScrollRef.current = false;
          lastSyncRef.current = new Date().toISOString();
        } else {
          toast.error(response.message);
        }
      })
      .catch((error) => {
        console.error("Error get GameSessionData:", error);
        toast.dismiss();
        toast.error("Error get yantra sessions");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const refreshSessionDelta = async () => {
    if (!lastSyncRef.current) return;

    const response = await gameSercApi.getGameSessionDelta(
      activeTab,
      activeDate,
      lastSyncRef.current,
    );

    if (response.isSuccess && response.data.length > 0) {
      setSessions((prev) => {
        const map = new Map(prev.map((s) => [s.id, s]));
        response.data.forEach((u) => {
          map.set(u.id, { ...map.get(u.id), ...u });
        });
        return Array.from(map.values());
      });

      lastSyncRef.current = response.serverTime; // ✅
    }
  };

  const updateMultiSessionPer = async () => {
    // Convert "10,21,51,60" → [10,21,51,60]
    const percentages = percentageInput
      .split(",")
      .map((p) => p.trim())
      .filter((p) => p !== "")
      .map(Number);

    // Reject empty or comma-only input (e.g. ",,,,,,")
    if (percentages.length === 0) {
      toast.error("Enter at least one percentage (e.g. 10,21,51,60)");
      return;
    }

    // Validation
    if (percentages.some(isNaN)) {
      toast.error("Invalid percentage value found");
      return null;
    }

    if (percentages.some((p) => p < 0 || p > 100)) {
      toast.error("Percentage must be between 0 and 100");
      return null;
    }

    const payload = {
      game_type: activeTab,
      date: activeDate,
      session_percentages: percentages,
    };
    console.log("payload >>>>>>", payload);
    if (!payload) return;
    setMultiUpdateLoader(true);
    gameSercApi
      .updateMultipleSessionPercentage(payload)
      .then((response) => {
        if (response.isSuccess) {
          toast.success(response.message);
          getGameSessionList();
        } else {
          toast.error(response.message);
        }
      })
      .catch((error) => {
        console.error("Error get updateMultiSessionPer:", error);
        toast.dismiss();
        toast.error("Error update yantra sessions!");
      })
      .finally(() => {
        setPercentageInput("");
        setMultiUpdateLoader(false);
      });
  };

  const allowEdit = activeDate !== "yesterday";

  const handleSave = (session_no) => {
    console.log("Save %", editValue, "for session", session_no);
    const payload = {
      id: session_no?.id,
      percentage: editValue,
    };
    setUpdateSessLoader(true);
    gameSercApi
      .updateSessionPercentage(payload)
      .then((response) => {
        if (response.isSuccess) {
          toast.success(response.message);
          setSessions((prev) =>
            prev.map((s) =>
              s.id === session_no.id ? { ...s, percentage: editValue } : s,
            ),
          );
        } else {
          toast.error(response.message);
        }
      })
      .catch((error) => {
        console.error("Error get updateSessionPercentage:", error);
        toast.dismiss();
        toast.error("Error update yantra sessions!");
      })
      .finally(() => {
        setPercentageInput("");
        setUpdateSessLoader(false);
        setEditing(null);
      });
  };

  // useEffect(() => {
  //   getGameSessionList();
  //   const interval = setInterval(() => {
  //     refreshSessionDelta();
  //   }, 30000);

  //   return () => clearInterval(interval);
  // }, [activeTab, activeDate]);
  useEffect(() => {
    let interval;

    const init = async () => {
      await getGameSessionList(); // ensures lastSyncRef is set

      interval = setInterval(() => {
        refreshSessionDelta();
      }, 30000);
    };

    init();

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeTab, activeDate]);

  // Auto-scroll to RUNNING session (or first upcoming PENDING) on load.
  // Goal: user immediately sees running/upcoming without manually scrolling.
  useEffect(() => {
    if (loading) return;
    if (didAutoScrollRef.current) return;
    if (!Array.isArray(sessions) || sessions.length === 0) return;

    const target =
      sessions.find((s) => String(s?.status).toUpperCase() === "RUNNING") ||
      sessions.find((s) => String(s?.status).toUpperCase() === "PENDING");
    if (!target?.id) return;

    const idx = sessions.findIndex((s) => Number(s?.id) === Number(target.id));
    if (idx >= 0 && idx >= visibleCount) {
      setVisibleCount(Math.min(sessions.length, idx + 20));
      return;
    }

    didAutoScrollRef.current = true;
    setTimeout(() => {
      const el = sessionCardRefs.current?.[target.id];
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 0);
  }, [loading, sessions, visibleCount]);

  return (
    <div className="game_session-page">
      {/* HEADER */}

      <div className="game_session-header">
        {/* GAME TYPE TABS */}
        <div className="d-flex align-items-center justify-content-between">
          <div className="tab-wrapper">
            <button
              className={activeTab === "3min" ? "tab active" : "tab"}
              onClick={() => setActiveTab("3min")}
            >
              3 Min Session
            </button>

            <button
              className={activeTab === "5min" ? "tab active" : "tab"}
              onClick={() => setActiveTab("5min")}
            >
              5 Min Session
            </button>
          </div>

          {/* DATE FILTER */}
          <div className="date-filter">
            {["yesterday", "today", "tomorrow"].map((day) => (
              <button
                key={day}
                className={activeDate === day ? "active" : ""}
                onClick={() => setActiveDate(day)}
              >
                {day.charAt(0).toUpperCase() + day.slice(1)}
              </button>
            ))}
          </div>
        </div>
        <div className="row mt-2 align-items-center g-2">
          <div className="col-8 col-md-4 col-lg-4">
            <input
              type="text"
              className="form-control"
              placeholder="Set session percentage ex: 10,21,51,60"
              value={percentageInput}
              onChange={(e) => {
                const v = e.target.value.replace(/[^0-9,]/g, "");
                setPercentageInput(v);
              }}
              disabled={multiUpdateLoader}
            />
          </div>

          <div className="col-3 col-md-3 col-lg-2 d-grid">
            <button
              type="button"
              className={`btn ${multiUpdateLoader ? "btn-secondary" : "btn-success"} d-flex align-items-center justify-content-center gap-2`}
              onClick={updateMultiSessionPer}
              disabled={multiUpdateLoader}
            >
              {multiUpdateLoader ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Applying...
                </>
              ) : (
                "Apply"
              )}
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <Loader message="Loading Session..." />
      ) : sessions.length === 0 ? (
        <NoData
          message="No Yantra Session Available"
          subText="Start by creating your yantra session or check back later"
        />
      ) : (
        <>
          {/* SESSION GRID */}
          <div className="session-grid">
            {sessions.slice(0, visibleCount).map((s) => {
              // const running = isRunning(s);

              return (
                <div
                  key={s.session_no}
                  ref={setSessionCardRef(s.id)}
                  data-session-id={s.id}
                  className={`session-card ${s.status === "RUNNING" ? "session-card-running" : ""}`}
                >
                  <div className="session-top">
                    <div className="d-flex align-items-center gap-2">
                      <span># {s.session_no}</span>
                    </div>

                    {/* STATUS BADGE */}
                    <span
                      className={`status-badge ${
                        s.status === "RUNNING"
                          ? STATUS_META.RUNNING.className
                          : STATUS_META[s.status]?.className || "status-pending"
                      }`}
                    >
                      {s.status}
                    </span>
                  </div>

                  <div className="percentage">
                    {editing === s.session_no ? (
                      <>
                        <input
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          type="number"
                          className="game_session_input"
                        />
                        <button
                          className="save_session-btn"
                          onClick={() => handleSave(s)}
                          disabled={updateSessLoader}
                        >
                          {updateSessLoader ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm"
                                role="status"
                                aria-hidden="true"
                              ></span>
                            </>
                          ) : (
                            <GiCheckMark size={18} />
                          )}
                        </button>
                      </>
                    ) : (
                      <>
                        <strong>{s.percentage}%</strong>
                        {allowEdit && s.status === "PENDING" ? (
                          <button
                            className="edit-btn"
                            onClick={() => {
                              setEditing(s.session_no);
                              setEditValue(s.percentage);
                            }}
                          >
                            <MdModeEdit size={18} />
                          </button>
                        ) : (
                          <div>
                            <div className="session-time">
                              {formatSessionTime(s.start_time, s.end_time)}
                              <button
                                type="button"
                                className="view-winning-detail-btn"
                                title="View winning detail"
                                onClick={() => {
                                  try {
                                    window.sessionStorage.setItem(
                                      "swd_session_id",
                                      String(s.id),
                                    );
                                  } catch (_) {}
                                  history.push("/session-winning-detail");
                                }}
                              >
                                <MdPreview size={18} />
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {/* LOAD MORE */}
          {visibleCount < sessions.length && (
            <div className="load-more">
              <button
                className="load-more-btn"
                onClick={() => setVisibleCount((v) => v + 60)}
              >
                Load More Sessions
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default GameSession;
